/**
 * The tokens a person is findable by.
 *
 * ## Why this exists
 *
 * Search was a Firestore range over `normalizedName`:
 *
 * ```
 * where("normalizedName", ">=", q)
 * where("normalizedName", "<=", q + "")
 * ```
 *
 * which is a **prefix** match on the whole string. "יובל חבאז" was findable by
 * "יובל" and not by "חבאז", and the product owner reported exactly that. It is
 * the wrong shape for a person: a surname is the half people remember, and in a
 * list of 3,894 the first name is the half that repeats.
 *
 * Firestore has no substring search, so the substrings have to exist as data
 * before they can be queried. Each person carries an array of the prefixes of
 * every word in their name — "חבאז" contributes `חב`, `חבא`, `חבאז` — and search
 * becomes `array-contains`, which is an index hit rather than a scan.
 *
 * ## Where the limits are, deliberately
 *
 * - **Two characters minimum.** One Hebrew letter matches a third of the
 *   database; that is not a search result, it is a page of noise.
 * - **Twelve characters maximum per prefix.** Nobody types the thirteenth letter
 *   of a surname to narrow a list that is already down to one.
 * - **`MAX_TOKENS` caps the array**, because Firestore charges an index entry per
 *   element and a pasted paragraph in a name field should not cost 400 of them.
 *
 * Phone digits and the email local part are included: "0503" and "yuval" are
 * both things people type into a search box expecting a person back.
 */
const MIN_PREFIX = 2;
const MAX_PREFIX = 12;
const MAX_TOKENS = 60;
/**
 * Strips Hebrew niqqud and the geresh/gershayim that make two spellings of one
 * name look like two names — "מנחם־מענדל" and "מנחם מענדל" have to tokenise the
 * same, or the search finds one and misses the other.
 */
/**
 * Folding happens before splitting, and the order is the whole point.
 *
 * Niqqud are combining marks, so they are neither letters nor digits — split
 * first and "שְׁמוּאֵל" comes apart into four fragments, none of which is the word
 * anybody types. Stripping them first leaves one word that matches the
 * unpointed spelling, which is how the name is written everywhere else.
 */
function fold(text) {
    return text
        .normalize("NFKD")
        // Combining marks by category, not by code range. The Hebrew block from
        // U+0591 to U+05C7 is mostly niqqud and cantillation but it also holds the
        // maqaf — "מנחם־מענדל" — and stripping that glued two names into one word
        // that nobody would ever type. `\p{Mn}` takes the marks and leaves the
        // punctuation for `words()` to split on, which is what it is for.
        .replace(/\p{Mn}/gu, "")
        .replace(/[׳״'"`]/g, "") // geresh, gershayim and their ASCII twins
        .toLowerCase();
}
/** Splits on anything that is not a letter or a digit, in any script. */
function words(text) {
    return fold(text)
        .split(/[^\p{L}\p{N}]+/u)
        .map((w) => w.trim())
        .filter((w) => w.length > 0);
}
function prefixesOf(word, out) {
    const capped = word.slice(0, MAX_PREFIX);
    for (let n = MIN_PREFIX; n <= capped.length; n++)
        out.add(capped.slice(0, n));
    // A one-letter word is still itself — an initial like "ר׳" should not vanish
    // entirely just because it is short.
    if (word.length === 1)
        out.add(word);
}
/**
 * Builds the array stored as `searchTokens`.
 *
 * Deterministic and sorted, so re-running the backfill over an unchanged person
 * produces an identical array and writes nothing.
 */
export function buildSearchTokens(parts) {
    const out = new Set();
    for (const w of words(parts.displayName ?? ""))
        prefixesOf(w, out);
    for (const value of parts.extra ?? []) {
        if (typeof value === "string")
            for (const w of words(value))
                prefixesOf(w, out);
    }
    for (const value of parts.phones ?? []) {
        if (typeof value !== "string")
            continue;
        // Israeli numbers are stored as +9725… and typed as 05…; index both endings
        // so either way of typing it finds the person.
        const digits = value.replace(/\D/g, "");
        if (digits.length < 4)
            continue;
        const local = digits.startsWith("972") ? `0${digits.slice(3)}` : digits;
        for (const form of new Set([digits, local])) {
            for (let n = MIN_PREFIX; n <= Math.min(form.length, MAX_PREFIX); n++)
                out.add(form.slice(0, n));
            // The last four digits are how people identify a number they half
            // remember.
            if (form.length >= 4)
                out.add(form.slice(-4));
        }
    }
    for (const value of parts.emails ?? []) {
        if (typeof value !== "string")
            continue;
        for (const w of words(value.split("@")[0] ?? ""))
            prefixesOf(w, out);
    }
    return [...out].sort().slice(0, MAX_TOKENS);
}
/**
 * Turns what somebody typed into the single token to query.
 *
 * One token, not several: `array-contains-any` would be an OR, and typing two
 * words should narrow a search rather than widen it. The longest word is used
 * because it is the most selective, and the rest are re-checked on the client
 * against the handful of rows that come back.
 */
export function searchToken(term) {
    const ws = words(term).filter((w) => w.length >= MIN_PREFIX);
    if (ws.length === 0)
        return null;
    const longest = ws.reduce((a, b) => (b.length > a.length ? b : a));
    return longest.slice(0, MAX_PREFIX);
}
/** The other words typed, for narrowing the result set on the client. */
export function searchRest(term) {
    const token = searchToken(term);
    return words(term).filter((w) => w.length >= MIN_PREFIX && w.slice(0, MAX_PREFIX) !== token);
}
/** True when a person's tokens satisfy every word that was typed. */
export function matchesAllWords(tokens, term) {
    const set = new Set(tokens);
    return words(term)
        .filter((w) => w.length >= MIN_PREFIX)
        .every((w) => set.has(w.slice(0, MAX_PREFIX)));
}
/**
 * The tokens an **activity** is findable by.
 *
 * ## The half of search that was missing
 *
 * "אם אני כותב שיעור גמרא, זה אמור להביא לי את כל מי שהיה בשיעור גמרא בציר הזמן"
 * — and it could not, because nothing but people was indexed. The subject of an
 * activity is often the only thing anybody remembers about it: not who came to
 * "שמות לברכה בציון הרבי", but that the list exists at all. 3,445 timeline
 * entries carried those subjects as plain text and no query could reach them.
 *
 * ## Title first, and that is what the cap is for
 *
 * The title is the subject — "מכירת חמץ", "רישום גן חב״ד תשפ״ו". The summary is
 * supporting detail: a category on an attendance record ("גן ילדים"), a method
 * on a donation ("מזומן"). Both are worth searching, so both are indexed, but
 * the title's tokens are laid down first so that a long summary can never push
 * the actual subject out of a capped array.
 *
 * ## Hebrew glues its prepositions onto the word
 *
 * The subject reads "פינוק **ל**יולדת / שפרה ופועה", and somebody looking for it
 * types "יולדת". Prefix tokens match from the start of a word, so that search
 * returned nothing against 138 entries that are exactly what was wanted —
 * verified against production, which is how this was found rather than guessed.
 * The same miss hides "שמות **ל**ברכה **ב**ציון הרבי" from both "ברכה" and
 * "ציון".
 *
 * So a word beginning with one of ה ו ב כ ל מ ש is *also* indexed without it.
 * Only for subjects, not for names: a subject is a phrase, where these letters
 * are usually prepositions, while "מנחם" is a name and its מ is just a letter.
 * The remainder must be at least three characters, which keeps "של" and "לי"
 * from contributing noise.
 *
 * This is cheap and it is not morphology. It will not find "ילדים" inside
 * "לילדים" beyond the one leading letter, and it does not try to.
 *
 * ## Digits in a summary are not a subject
 *
 * A donation summary reads "3,000 ₪". Indexed naively that contributes `00`,
 * `000`, `30` and `300` — tokens that match nothing anybody is looking for and
 * fill the cap with noise. Numbers in the *title* are kept, because a year is
 * part of a name: "תשפ״ו" is how you find that particular registration.
 */
/** Lower than a person's, because there are an order of magnitude more of these
 *  and each element is a billable index entry. Forty covers a long title and a
 *  category with room to spare. */
const MAX_ACTIVITY_TOKENS = 40;
/** The one-letter prepositions and conjunctions Hebrew writes joined to the
 *  following word: the bet, kaf, lamed, mem, shin, he and vav. */
const HEBREW_PROCLITICS = new Set(["ב", "כ", "ל", "מ", "ש", "ה", "ו"]);
/** Minimum length of what is left after stripping one, below which the
 *  remainder is a fragment rather than a word. */
const MIN_STEM = 3;
function indexWord(word, out) {
    prefixesOf(word, out);
    if (word.length > MIN_STEM && HEBREW_PROCLITICS.has(word[0])) {
        prefixesOf(word.slice(1), out);
    }
}
export function buildActivityTokens(activity) {
    const fromTitle = new Set();
    for (const w of words(activity.title ?? ""))
        indexWord(w, fromTitle);
    const fromSummary = new Set();
    for (const w of words(activity.summary ?? "")) {
        if (/^\p{N}+$/u.test(w))
            continue; // see above: an amount is not a subject
        indexWord(w, fromSummary);
    }
    // Sorted within each group rather than across both, so the ordering is
    // deterministic — a re-run of the backfill over unchanged data must produce an
    // identical array and write nothing — while the title still wins the cap.
    const out = [...fromTitle].sort();
    for (const token of [...fromSummary].sort())
        if (!fromTitle.has(token))
            out.push(token);
    return out.slice(0, MAX_ACTIVITY_TOKENS);
}
//# sourceMappingURL=searchTokens.js.map